import React, { useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { Modal, Typography, TypographyVariant } from 'components/core';
import { useRemoveBankAccount } from 'modules/2023-q3/banking-page/v2/hooks/use-remove-bank-account';
import { useRemoveBankItem } from 'modules/2023-q3/banking-page/v2/hooks/use-remove-bank-item';
import { FeatureFlag, FeatureFlags } from 'utils/feature-flags';
import { UserRole } from 'modules/2023-q4/rbac/enums/user-role';
import { UserPermissionsRequired } from 'modules/2023-q4/rbac/components/user-permissions-required/user-permissions-required';
import { Styled } from './remove-bank-account-action.style';

const REMOVE_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support if the issue persists.';

interface ConfirmRemoveModalProps {
  confirmLabel: string;
  errorMessage: string | null;
  isLoading: boolean;
  message: React.ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
}

const ConfirmRemoveModal: React.FC<ConfirmRemoveModalProps> = ({
  confirmLabel,
  errorMessage,
  isLoading,
  message,
  onClose,
  onConfirm,
  open,
  title,
}) => (
  <Modal onClose={onClose} open={open}>
    <Box p={4}>
      <Typography variant={TypographyVariant.Header4}>{title}</Typography>

      <Box my={3}>
        <Typography variant={TypographyVariant.Paragraph1}>
          {message}
        </Typography>
      </Box>

      {errorMessage && (
        <Box mb={3}>
          <Typography color="amaranth" variant={TypographyVariant.Paragraph2}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      <Grid container justify="flex-end" spacing={2}>
        <Grid item>
          <Styled.ModalCancelButton onClick={onClose} variant="text">
            Cancel
          </Styled.ModalCancelButton>
        </Grid>
        <Grid item>
          <Styled.ModalConfirmButton disabled={isLoading} onClick={onConfirm}>
            {confirmLabel}
          </Styled.ModalConfirmButton>
        </Grid>
      </Grid>
    </Box>
  </Modal>
);

interface AdminOnlyBankingActionProps {
  children: React.ReactNode;
}

const AdminOnlyBankingAction: React.FC<AdminOnlyBankingActionProps> = ({
  children,
}) => (
  <FeatureFlag
    disabled={<>{children}</>}
    enabled={
      <UserPermissionsRequired roles={[UserRole.ADMIN]}>
        {children}
      </UserPermissionsRequired>
    }
    flag={FeatureFlags.REACT_APP_2023_Q4_USER_ROLES_ENABLED}
  />
);

interface RemoveBankAccountActionProps {
  accountId: string;
  accountName: string;
  accountMask: string;
}

export const RemoveBankAccountAction: React.FC<RemoveBankAccountActionProps> =
  ({ accountId, accountName, accountMask }) => {
    const [open, setOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const removeBankAccount = useRemoveBankAccount();

    const handleClose = () => {
      setOpen(false);
      setErrorMessage(null);
    };

    const handleOpen = () => {
      setErrorMessage(null);
      setOpen(true);
    };

    const handleRemove = async () => {
      setErrorMessage(null);

      try {
        await removeBankAccount.mutateAsync({ accountId });
        handleClose();
      } catch {
        setErrorMessage(REMOVE_ERROR_MESSAGE);
      }
    };

    return (
      <AdminOnlyBankingAction>
        <>
          <Styled.AccountRemoveLink onClick={handleOpen}>
            Remove
          </Styled.AccountRemoveLink>

          <ConfirmRemoveModal
            confirmLabel="Remove"
            errorMessage={errorMessage}
            isLoading={removeBankAccount.isLoading}
            message={
              <>
                Are you sure you want to remove <b>{accountName}</b> ending in{' '}
                {accountMask}? This will remove the account from Treasure. The
                account can be added back at any time.
              </>
            }
            onClose={handleClose}
            onConfirm={handleRemove}
            open={open}
            title="Remove bank account"
          />
        </>
      </AdminOnlyBankingAction>
    );
  };

interface RemoveBankConnectionActionProps {
  bankItemId: string;
  institutionName: string;
}

export const RemoveBankConnectionAction: React.FC<RemoveBankConnectionActionProps> =
  ({ bankItemId, institutionName }) => {
    const [open, setOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const removeBankItem = useRemoveBankItem();

    const handleClose = () => {
      setOpen(false);
      setErrorMessage(null);
      (document.activeElement as HTMLElement | null)?.blur();
    };

    const handleOpen = () => {
      setErrorMessage(null);
      setOpen(true);
    };

    const handleRemove = async () => {
      setErrorMessage(null);

      try {
        await removeBankItem.mutateAsync({ bankItemId });
        handleClose();
      } catch {
        setErrorMessage(REMOVE_ERROR_MESSAGE);
      }
    };

    return (
      <AdminOnlyBankingAction>
        <>
          <Styled.ConnectionRemoveButton
            onClick={handleOpen}
            variant="outlined"
          >
            Remove connection
          </Styled.ConnectionRemoveButton>

          <ConfirmRemoveModal
            confirmLabel="Remove connection"
            errorMessage={errorMessage}
            isLoading={removeBankItem.isLoading}
            message={
              institutionName ? (
                <>
                  Are you sure you want to remove your <b>{institutionName}</b>{' '}
                  Plaid connection and all accounts linked from it? The Plaid
                  connection can be re-established at any time.
                </>
              ) : (
                <>
                  Are you sure you want to remove this institution&apos;s Plaid
                  connection and all accounts linked from it? The Plaid
                  connection can be re-established at any time.
                </>
              )
            }
            onClose={handleClose}
            onConfirm={handleRemove}
            open={open}
            title="Remove Plaid connection"
          />
        </>
      </AdminOnlyBankingAction>
    );
  };
